// Browser-friendly Fountain parser. Mirrors the canonical Script
// shape produced by src/main/parsers/fountain.js, as ESM with no Node
// dependencies. Built on `fountain-js` (already in deps).

import { Fountain } from 'fountain-js';
import {
  uuid,
  newScene,
  newBlock,
  recordCharacter,
  recordDialogue,
  characterKey,
  extractLastslateId,
} from './shared.js';

const TOKEN_TYPE_MAP = Object.freeze({
  scene_heading: 'scene-heading',
  action: 'action',
  character: 'character',
  parenthetical: 'parenthetical',
  dialogue: 'dialogue',
  transition: 'transition',
  centered: 'centered',
});

const TITLE_TOKEN_TYPES = new Set([
  'title',
  'author',
  'authors',
  'credit',
  'source',
  'notes',
  'draft_date',
  'date',
  'contact',
  'copyright',
]);

function tokenIsTitle(t) {
  return t && (t.is_title === true || TITLE_TOKEN_TYPES.has(t.type));
}

export function parseFountain(rawText) {
  if (typeof rawText !== 'string') {
    throw new TypeError(`parseFountain expects a string, got ${typeof rawText}`);
  }

  const fountain = new Fountain();
  const parsed = fountain.parse(rawText, true);
  const tokens = (parsed && parsed.tokens) || [];

  const titlePage = {};
  let title = (parsed && parsed.title) || 'Untitled';
  for (const t of tokens) {
    if (!tokenIsTitle(t)) continue;
    const key = (t.type === 'authors' ? 'author' : t.type || '').toLowerCase();
    const value = (t.text || '').trim();
    if (key && value) titlePage[key] = value;
  }
  if (titlePage.title) title = titlePage.title;

  const scenes = [];
  const prelude = [];
  const characters = {};
  let currentScene = null;
  let sceneNumber = 0;
  let lastCharacter = null;
  let inDialogueBlock = false;

  for (const tok of tokens) {
    if (tokenIsTitle(tok)) continue;

    switch (tok.type) {
      case 'scene_heading': {
        sceneNumber += 1;
        currentScene = newScene(sceneNumber, (tok.text || '').trim());
        scenes.push(currentScene);
        lastCharacter = null;
        inDialogueBlock = false;
        break;
      }
      case 'dialogue_begin':
        inDialogueBlock = true;
        break;
      case 'dialogue_end':
        inDialogueBlock = false;
        lastCharacter = null;
        break;
      case 'dual_dialogue_begin':
      case 'dual_dialogue_end':
        break;

      case 'character':
      case 'action':
      case 'parenthetical':
      case 'dialogue':
      case 'transition':
      case 'centered': {
        const mappedType = TOKEN_TYPE_MAP[tok.type];
        if (!mappedType) break;
        const text = (tok.text || '').trim();
        if (!text) break;

        if (!currentScene) {
          prelude.push(newBlock(mappedType, text));
          if (mappedType === 'character') {
            lastCharacter = characterKey(text);
            recordCharacter(characters, lastCharacter, null);
          } else if (mappedType === 'dialogue' && lastCharacter) {
            recordDialogue(characters, lastCharacter, text);
          } else if (mappedType !== 'parenthetical' && !inDialogueBlock) {
            lastCharacter = null;
          }
          break;
        }

        currentScene.blocks.push(newBlock(mappedType, text));

        if (mappedType === 'character') {
          lastCharacter = characterKey(text);
          recordCharacter(characters, lastCharacter, currentScene);
        } else if (mappedType === 'dialogue' && lastCharacter) {
          recordDialogue(characters, lastCharacter, text);
        } else if (mappedType !== 'parenthetical' && !inDialogueBlock) {
          lastCharacter = null;
        }
        break;
      }

      case 'section':
      case 'synopsis':
      case 'note':
      case 'boneyard_begin':
      case 'boneyard_end':
      case 'page_break':
      case 'line_break':
        break;

      default:
        break;
    }
  }

  return { title, titlePage, prelude, scenes, characters };
}

export function buildScript(rawText, { filePath = null, title: titleOverride, format } = {}) {
  const parsed = parseFountain(rawText);
  // Pick up the Lastslate-ID embedded in the title block so the
  // editor's doc-store opens the right Y.Doc and notes/comments key
  // off the canonical script identity.
  const lastslateId = extractLastslateId(rawText);
  return buildScriptFromRawParse(parsed, {
    filePath,
    title: titleOverride,
    format,
    lastslateId,
  });
}

export function buildScriptFromRawParse(
  parsed,
  { filePath = null, title: titleOverride, format, lastslateId = null } = {},
) {
  const baseName = filePath ? filePath.replace(/^.*\//, '').replace(/\.[^.]+$/, '') : parsed.title;
  return {
    id: uuid(),
    filePath: filePath || null,
    format: format || 'fountain',
    // Canonical doc identity — used by doc-store.openDraft and the
    // notes/comments stores. Null when the file hasn't been promoted
    // to a cloud draft yet; the cloud bind path mints one.
    lastslateId: lastslateId || null,
    title: titleOverride || (parsed.title && parsed.title !== 'Untitled' ? parsed.title : baseName),
    titlePage: parsed.titlePage || {},
    prelude: parsed.prelude || [],
    scenes: parsed.scenes,
    characters: parsed.characters,
    sceneCount: parsed.scenes.length,
    loadedAt: new Date().toISOString(),
  };
}
